import React, { useCallback, useEffect } from 'react';
import { StyleSheet, View } from 'react-native';
import {
  useFocusEffect,
  useIsFocused,
  useNavigation,
} from '@react-navigation/native';
import {
  BodyText,
  Icon,
  LoadingAnimation,
  ModalDialogue,
  theme,
  useResponsive,
  useToggleState,
} from '@sg/garnish';
import { RedemptionChannel } from '@sg/graphql-schema';

import { useIsLoggedIn } from '@order/AuthMachine';
import { AppFooter, SignedOutView } from '@order/components';
import { Loyalty, LOYALTY_PALETTES } from '@order/features/loyalty';
import {
  useLoyaltyContent,
  useLoyaltyInfoBenefits,
  useLoyaltyPremiumStatus,
  useNavigateToLastOrderMenu,
} from '@order/hooks';
import { useTelemetry, useTrackEffect } from '@order/Telemetry';

import { LoyaltyOfferCard } from '../../components/LoyaltyOffers/components';
import { JoinSgRewardsScreen } from '../JoinSgRewardsScreen';
import {
  LoyaltyHomeFaqXs,
  LoyaltyHowItWorksModal,
  LoyaltyPointHistoryModal,
} from './components';
import {
  useLoyaltyHomeScreenReferrer,
  useLoyaltyInfo,
  useTermsBeingShown,
} from './hooks';
import { useHomeProgressBenefit } from './hooks';
import { useOptInChallengeOnFocus } from './hooks/useOptInChallengeOnFocus';

// ─────────────────────────────────────────────────────────────────────────────

export const LoyaltyHomeScreen = () => {
  const { currentBreakpoint, minWidth, match } = useResponsive();
  const isLoggedIn = useIsLoggedIn();
  const isScreenFocused = useIsFocused();

  // ─── Auto Opt In ─────────────────────────────────────────────────────

  useOptInChallengeOnFocus();

  // ─── Premium ──────────────────────────────────────────────────────────────

  const { isPremium } = useLoyaltyPremiumStatus();
  const premiumBgColor = isPremium ? LOYALTY_PALETTES.premiumBgColor : null;
  const premiumFgColor = isPremium ? LOYALTY_PALETTES.premiumFgColor : null;

  // ─── GraphQL ──────────────────────────────────────────────────────────────

  const {
    isLoading: isLoyaltyInfoLoading,
    isStale: isLoyaltyInfoStale,
    hasFailedToLoad,
    firstName,
    availablePoints,
    pendingPoints,
    totalPoints,
    challenges,
    rewards,
    pointsRewards,
    isOptedOut,
    refetch: refetchLoyaltyInfo,
  } = useLoyaltyInfo({ pause: !isLoggedIn || !isScreenFocused });

  const hasChallenges = challenges.length > 0;
  const hasRewards = rewards.length > 0;

  // ─── Styles ───────────────────────────────────────────────────────────────

  const rewardContainer = match([styles.rewardContainerXS, undefined]);

  const challengesWithRewardsSpacing = hasRewards
    ? match([styles.challengesWithRewardsXS, styles.challengesWithRewardsSM])
    : undefined;

  const rewardsWithChallengesSpacing = hasChallenges
    ? match([styles.rewardsWithChallengesXS, styles.rewardsWithChallengesSM])
    : undefined;

  // ─── Contentful ───────────────────────────────────────────────────────────

  const { homeHowItWorksSM, homeProgressBenefits, termsAndConditionsUrl } =
    useLoyaltyContent({
      availablePoints,
    });

  // ─── Progress Benefit ─────────────────────────────────────────────────────

  const progressBenefit = useHomeProgressBenefit({
    availablePoints,
    pendingPoints,
    homeProgressBenefits,
  });

  // ─── Loyalty Info Benefits ────────────────────────────────────────────────

  const {
    benefits: loyaltyInfoBenefits,
    isLoading: isFetchingBenefits,
    hasError: hasBenefitsError,
    refetch: refetchBenefits,
  } = useLoyaltyInfoBenefits();

  // ─── Points History Modal ─────────────────────────────────────────────────

  const {
    value: isShowingPointsHistoryModal,
    toggleOn: showPointsHistoryModalToggleOn,
    toggleOff: hidePointsHistoryModal,
  } = useToggleState();

  // ─── How it Works Modal ───────────────────────────────────────────────────

  const {
    value: isShowingHowItWorksModal,
    toggleOn: showHowItWorksModalToggleOn,
    toggleOff: hideHowItWorksModal,
  } = useToggleState();

  // ─── Telemetry ────────────────────────────────────────────────────────────

  useTrackEffect('loyalty.home.view');
  const { track } = useTelemetry();

  const showPointsHistoryModal = useCallback(() => {
    track('loyalty.home.points_history.open');
    showPointsHistoryModalToggleOn();
  }, [track, showPointsHistoryModalToggleOn]);

  const showHowItWorksModal = useCallback(() => {
    track('loyalty.home.how_it_works.open');
    showHowItWorksModalToggleOn();
  }, [track, showHowItWorksModalToggleOn]);

  const trackBenefitsOpened = useCallback(() => {
    track('loyalty.home.benefits.open');
  }, [track]);

  // ─── Terms Modal ──────────────────────────────────────────────────────────

  const { termsBeingShown, showTerms, hideTerms } = useTermsBeingShown();

  // ─── Navigation ───────────────────────────────────────────────────────────

  const navigation = useNavigation();
  const navigateToLastOrderMenu = useNavigateToLastOrderMenu();

  const clearModals = useCallback(() => {
    hideTerms();
    hidePointsHistoryModal();
    hideHowItWorksModal();
  }, [hideHowItWorksModal, hidePointsHistoryModal, hideTerms]);

  const startOrder = useCallback(() => {
    clearModals();
    navigateToLastOrderMenu();
  }, [clearModals, navigateToLastOrderMenu]);

  useEffect(() => {
    navigation.setOptions({ headerShown: !currentBreakpoint.isXS });
  }, [navigation, currentBreakpoint.isXS]);

  useFocusEffect(
    useCallback(() => {
      return clearModals;
    }, [clearModals]),
  );

  useLoyaltyHomeScreenReferrer();

  // ──────────────────────────────────────────────────────────────────────────

  if (!isLoggedIn) return <SignedOutView />;

  if (hasFailedToLoad) {
    return (
      <Loyalty.Home.ErrorState
        isLoading={isLoyaltyInfoLoading || isLoyaltyInfoStale}
        refetch={refetchLoyaltyInfo}
      />
    );
  }

  if (isLoyaltyInfoLoading) return <LoadingAnimation />;

  if (isOptedOut) {
    return <JoinSgRewardsScreen />;
  }

  // ──────────────────────────────────────────────────────────────────────────

  return (
    <Loyalty.Home.Container
      availablePoints={availablePoints}
      palette={isPremium ? 'dark-kale' : 'cream'}
      style={premiumBgColor}
    >
      <Loyalty.Home.Header.Container>
        <Loyalty.Home.Header.Content>
          <Loyalty.Home.Header.FirstName
            firstName={firstName}
            style={premiumFgColor}
          />

          <Loyalty.Home.Header.AvailablePoints
            availablePoints={availablePoints}
            style={premiumFgColor}
          />
        </Loyalty.Home.Header.Content>

        {currentBreakpoint.isXS && isPremium ? (
          <Icon name="IconGoat" width={60} height={60} />
        ) : null}
      </Loyalty.Home.Header.Container>

      {progressBenefit && loyaltyInfoBenefits.length > 0 && minWidth.isSM ? (
        <Loyalty.Home.ProgressBenefit.SM
          progressBenefit={progressBenefit}
          style={premiumFgColor}
        />
      ) : null}

      {pointsRewards.length > 0 ? (
        <Loyalty.Home.ProgressBar
          palette={isPremium ? 'dark' : 'light'}
          availablePoints={availablePoints}
          totalPoints={totalPoints}
          pointsRewards={pointsRewards}
        />
      ) : null}

      {progressBenefit && loyaltyInfoBenefits.length > 0 && !minWidth.isSM ? (
        <Loyalty.Home.ProgressBenefit.XS
          palette={isPremium ? 'dark' : 'light'}
          progressBenefit={progressBenefit}
          benefitsCount={loyaltyInfoBenefits.length}
          onBenefitsOpened={trackBenefitsOpened}
        >
          <Loyalty.InfoBenefits
            availablePoints={availablePoints}
            loyaltyInfoBenefits={loyaltyInfoBenefits}
            isLoading={isFetchingBenefits}
            hasError={hasBenefitsError}
            style={Loyalty.Home.ProgressBenefit.BenefitsStyle}
            refetch={refetchBenefits}
          />
        </Loyalty.Home.ProgressBenefit.XS>
      ) : null}

      <Loyalty.Home.Body>
        {minWidth.isSM ? (
          <Loyalty.PointsHistory.CtaButton
            palette={isPremium ? 'light' : 'secondary'}
            onPress={showPointsHistoryModal}
          />
        ) : null}

        {currentBreakpoint.isXS ? (
          <>
            <Loyalty.PointsHistory.CtaLink
              style={isPremium ? LOYALTY_PALETTES.premiumFgColor : undefined}
              onPress={showPointsHistoryModal}
            />

            <BodyText
              style={isPremium ? LOYALTY_PALETTES.premiumFgColor : undefined}
              sizeMatch={['14']}
            >
              •
            </BodyText>

            <Loyalty.HowItWorks.Cta
              style={isPremium ? LOYALTY_PALETTES.premiumFgColor : undefined}
              onPress={showHowItWorksModal}
            />
          </>
        ) : null}
      </Loyalty.Home.Body>

      {hasChallenges ? (
        <Loyalty.Home.Challenges.Container
          style={[premiumBgColor, challengesWithRewardsSpacing]}
        >
          <Loyalty.Home.Challenges.Rail>
            {challenges.map((offer) => (
              <View
                key={offer.id}
                style={match([undefined, styles.offerCardContainerSM])}
              >
                <LoyaltyOfferCard
                  offer={offer}
                  rounded={true}
                  layout="compact"
                />
              </View>
            ))}
          </Loyalty.Home.Challenges.Rail>
        </Loyalty.Home.Challenges.Container>
      ) : (
        <Loyalty.Home.ChallengesEmptyState />
      )}

      {hasRewards ? (
        <Loyalty.Home.Rewards.Container
          style={[premiumBgColor, rewardsWithChallengesSpacing]}
        >
          <Loyalty.Home.Rewards.Rail>
            {rewards.map((reward) => {
              const isInStoreOnly =
                reward.redemptionChannel === RedemptionChannel.InStore;
              const onShowTerms = reward.terms ? showTerms(reward) : undefined;
              const onStartOrder = isInStoreOnly ? undefined : startOrder;

              return (
                <Loyalty.Reward
                  key={reward.id}
                  id={reward.id}
                  title={reward.name}
                  imageUrl={reward.assetUrl}
                  expirationDate={reward.expirationDate}
                  redeemableAt={reward.redeemableAt}
                  isInStoreOnly={isInStoreOnly}
                  style={rewardContainer}
                  onShowTerms={onShowTerms}
                  onStartOrder={onStartOrder}
                />
              );
            })}
          </Loyalty.Home.Rewards.Rail>
        </Loyalty.Home.Rewards.Container>
      ) : (
        <Loyalty.Home.RewardsEmptyState />
      )}

      {currentBreakpoint.isXS ? (
        <LoyaltyHomeFaqXs
          palette={isPremium ? 'white' : 'kale'}
          showHowItWorksModal={showHowItWorksModal}
        />
      ) : null}

      {minWidth.isSM ? (
        <Loyalty.Home.HowItWorks.Container>
          {homeHowItWorksSM.map((entry, index) => (
            <Loyalty.Home.HowItWorks.Card
              index={index + 1}
              key={entry.title}
              title={entry.title}
              description={entry.description}
            />
          ))}
        </Loyalty.Home.HowItWorks.Container>
      ) : null}

      <Loyalty.Home.Footer
        style={premiumBgColor}
        termsAndConditionsUrl={termsAndConditionsUrl}
      />

      {minWidth.isSM ? <AppFooter /> : null}

      <LoyaltyPointHistoryModal
        availablePoints={availablePoints}
        visible={isShowingPointsHistoryModal}
        startOrder={startOrder}
        onRequestClose={hidePointsHistoryModal}
      />

      <LoyaltyHowItWorksModal
        visible={isShowingHowItWorksModal}
        onRequestClose={hideHowItWorksModal}
      />

      <ModalDialogue
        visible={Boolean(termsBeingShown)}
        onRequestClose={hideTerms}
      >
        {termsBeingShown?.terms ? (
          <Loyalty.RewardTerms
            title={termsBeingShown.name}
            terms={termsBeingShown?.terms}
            expirationDate={termsBeingShown?.expirationDate}
          />
        ) : null}
      </ModalDialogue>
    </Loyalty.Home.Container>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  offerCardContainerSM: {
    width: 343,
    flexGrow: 1,
  },
  challengesWithRewardsXS: {
    paddingBottom: theme.spacing['2'],
  },
  challengesWithRewardsSM: {
    paddingBottom: theme.spacing['10'],
  },
  rewardsWithChallengesXS: {
    paddingTop: theme.spacing['2'],
  },
  rewardsWithChallengesSM: {
    paddingTop: theme.spacing['10'],
  },
  rewardContainerXS: {
    width: '100%',
  },
});
